<template>
  <div
    class="video"
    ref="video"
    @mousemove="handleOverlay"
    @click="playVideoClick"
  >
    <div
      class="flat-btn play-btn"
      ref="playVideo"
    >
      <i class="fas fa-play"></i>
    </div>
    <video
      class="video-player"
      ref="player"
      preload
      loop
      @canplay="updateVideoInfo"
      @timeupdate="updateVideoInfo"
      :src="'http://commondatastorage.googleapis.com/gtv-videos-bucket/sample/Sintel.mp4'"
      :poster="poster"
      @click="handlePlayPause"
    ></video>
    <div class="live" ref="live">
      <div class="circle"></div>
      <span>LIVE</span>
    </div>
    <div class="spinner-holder" ref="spinner">
      <div class="lds-spinner">
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
      </div>
    </div>
    <nav class="video-navbar" ref="videoNavbar">
      <div class="navbar-left">
        <span class="volume-span">
          <button @click="volumeNo">
            <i ref="volumeIcon" class="fas fa-volume"></i>
            <span class="hover-btn">{{ $t("video_player.volume") }}</span>
          </button>
          <input
            type="range"
            :style="{
              background: `linear-gradient(to right, #ffffff 0% , #ffffff ${
                rangeValue == null ? 50 : rangeValue * 100
              }%  , #595959 0%, #595959 100%)`,
            }"
            v-model="rangeValue"
            class="input-range"
            min="0"
            step="0.05"
            max="1"
          />
        </span>
        <span class="stop-pause">
          <button @click="handlePlayPause">
            <i class="fas fa-pause" ref="iconStopPause"></i>
            <span class="hover-btn">{{ $t("video_player.start_pauza") }}</span>
          </button>
        </span>
        <span>
          <button @click="handlePul">
            <i class="fas fa-chalkboard"></i>
            <span class="hover-btn"> {{ $t("video_player.mini_screen") }}</span>
          </button>
          <button @click="handleFullscreen" ref="fullBtn">
            <i class="fas fa-expand"></i>
            <span class="hover-btn">{{ $t("video_player.full_screen") }}</span>
          </button>
        </span>
      </div>
      <div class="navbar-right">
        <span>
          <button @click="$router.go(-1)">
            <i class="fas fa-reply"></i>
            <span class="hover-btn">{{ $t("main.return") }}</span>
          </button>
        </span>
      </div>
    </nav>
  </div>
</template>

<script>
import Hls from "hls.js";
import { onBeforeUnmount, onMounted, ref, watch } from "vue";

export default {
  name: "VideoPlayerLive",
  props: {
    url: {
      type: String,
      required: true,
    },
    poster: {
      type: String,
      required: true,
    },
  },
  setup() {
    const rangeValue = ref(null);
    const video = ref(null);
    const volumeIcon = ref(null);
    const iconStopPause = ref(null);
    const videoNavbar = ref(null);
    const live = ref(null);
    const spinner = ref(null);
    const player = ref(null);
    const duration = ref(null);
    const currentTime = ref(0);
    const playVideo = ref(null);
    const fullBtn = ref(null);

    let runvideo = 0;
    const volumeChange = () => {
      player.value.volume = rangeValue.value;
      if (rangeValue.value == null) {
        player.value.volume = 0.5;
      }
      if (rangeValue.value > 0.75) {
        volumeIcon.value.classList = "fas fa-volume-up";
      } else if (rangeValue.value < 0.75 && rangeValue.value > 0.5) {
        volumeIcon.value.classList = "fas fa-volume";
      } else if (rangeValue.value < 0.5 && rangeValue.value != 0) {
        volumeIcon.value.classList = "fas fa-volume-down";
      } else if (rangeValue.value == 0) {
        volumeIcon.value.classList = "fas fa-volume-mute";
      }
    };

    const volumeNo = () => {
      if (rangeValue.value == 0) {
        rangeValue.value = 0.5;
        player.value.volume = 0.5;
        volumeIcon.value.classList = "fas fa-volume-down";
      } else {
        rangeValue.value = 0;
        player.value.volume = 0;
        volumeIcon.value.classList = "fas fa-volume-mute";
      }
    };

    const handlePlayPause = () => {
      if (runvideo !== 0) {
        player.value.paused ? player.value.play() : player.value.pause();
        iconStopPause.value.className = "fas fa-pause";
        if (player.value.paused) {
          iconStopPause.value.className = "fas fa-play";
        }
      }
    };

    const updateVideoInfo = () => {
      try {
        duration.value = player.value.duration;
        currentTime.value = player.value.currentTime;

        if (player.value.play) {
          spinner.value.style.display = "none";
        } else {
          spinner.value.style.display = "inset";
        }
      } catch {
        console.log("err");
      }
    };

    const playVideoClick = () => {
      if (runvideo == 0) {
        videoNavbar.value.style.opacity = "1";
        playVideo.value.style.display = "none";
        player.value.play();
      }
      runvideo = 1;
    };

    let overlayTimeout;
    const handleOverlay = () => {
      if (typeof window !== 'undefined') {
        if (runvideo !== 0) {
          video.value.style.cursor = "default";
          videoNavbar.value.style.opacity = "1";
          live.value.style.opacity = "1";
          if (overlayTimeout) clearTimeout(overlayTimeout);
          overlayTimeout = setTimeout(() => {
            video.value.style.cursor = "none";
            live.value.style.opacity = "0";
            videoNavbar.value.style.opacity = "0";
          }, 2000);
        }
      }
    };

    const handlePul = () => {
      if (!document.pictureInPictureElement) {
        player.value.requestPictureInPicture().catch((error) => {
          // Video failed to enter Picture-in-Picture mode.
          console.log(error);
        });
      } else {
        document.exitPictureInPicture().catch((error) => {
          // Video failed to leave Picture-in-Picture mode.
          console.log(error);
        });
      }
    };

    const handleFullscreen = () => {
      screen.orientation.lock("landscape-primary");
      const elem = document.documentElement;
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.mozRequestFullScreen) {
        /* Firefox */
        elem.mozRequestFullScreen();
      } else if (elem.webkitRequestFullscreen) {
        /* Chrome, Safari & Opera */
        elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        /* IE/Edge */
        elem.msRequestFullscreen();
      }

      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    };

    onMounted(() => {
      handlePlayPause();
      volumeChange();
      player.value.pause();
      if (Hls.isSupported()) {
        const hls = new Hls();
        hls.loadSource("https://streaming.sevimliplay.tv/eyAic2Vzc2lvbl9pZCI6ImFhMWQxNGJmLTA5ZGItNGJmZC04ZGY0LTdkOGIyNjU4MTMzMCIsICJkb21haW5fbmFtZSI6InN0cmVhbWluZy5zZXZpbWxpcGxheS50diIsICJpcF9hZGRyZXNzIjoiMTk1LjE1OC4yNC4xMTYiLCAiZXhwaXJhdGlvbl9kYXRlIjoiMjA1MC0wMi0wMlQxMjowMDowMFoiLCAic3RyZWFtX25hbWUiOiIxIiwgInN0cmVhbV9wYXRoIjoiL2lwX3Y1IiwgImRhdGFjZW50ZXIiOiJzZXYiLCAiZHJtIjoic3BidHZjYXMiLCAicHJvdG9jb2wiOiJobHMiIH0=/MCwCFDkKX1RQeMiZXukHUEtoJUfCR3hGAhRDf5LdOT4ZxIGPXzCOfJ0IuORf1Q/ip_v5/1.m3u8");
        hls.attachMedia(player.value);
      } else if (player.value.canPlayType("application/vnd.apple.mpegurl")) {
        player.value.src =
          "https://streaming.sevimliplay.tv/eyAic2Vzc2lvbl9pZCI6ImFhMWQxNGJmLTA5ZGItNGJmZC04ZGY0LTdkOGIyNjU4MTMzMCIsICJkb21haW5fbmFtZSI6InN0cmVhbWluZy5zZXZpbWxpcGxheS50diIsICJpcF9hZGRyZXNzIjoiMTk1LjE1OC4yNC4xMTYiLCAiZXhwaXJhdGlvbl9kYXRlIjoiMjA1MC0wMi0wMlQxMjowMDowMFoiLCAic3RyZWFtX25hbWUiOiIxIiwgInN0cmVhbV9wYXRoIjoiL2lwX3Y1IiwgImRhdGFjZW50ZXIiOiJzZXYiLCAiZHJtIjoic3BidHZjYXMiLCAicHJvdG9jb2wiOiJobHMiIH0=/MCwCFDkKX1RQeMiZXukHUEtoJUfCR3hGAhRDf5LdOT4ZxIGPXzCOfJ0IuORf1Q/ip_v5/1.m3u8";
      }
    });

    onBeforeUnmount(() => {
      document.exitPictureInPicture();
    });

    watch(rangeValue, () => {
      volumeChange();
    });

    return {
      rangeValue,
      volumeChange,
      volumeNo,
      volumeIcon,
      iconStopPause,
      videoNavbar,
      handleOverlay,
      video,
      live,
      spinner,
      player,
      handlePlayPause,
      updateVideoInfo,
      duration,
      currentTime,
      playVideoClick,
      playVideo,
      handleFullscreen,
      fullBtn,
      handlePul,
    };
  },
};
</script>

<style lang="scss" scoped>
.play-btn {
  color: #fff;
  font-size: 1.625em;
  padding: 0.22em 1.2em;
  position: absolute;
  border: 1px solid #fff;
  transition: all 0.4s ease, border 0s;
  z-index: 200;

  &:hover {
    color: #121212;
    background-color: #fff;
    border: none;
  }
}

.none {
  display: none;
}
.video {
  width: 100%;
  height: 100vh;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: hidden;
  background-color: black;

  .spinner-holder {
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    z-index: 100;
    pointer-events: none;
  }
  .live {
    display: flex;
    position: absolute;
    align-items: center;
    bottom: 2em;
    left: 2em;
    z-index: 999;
    opacity: 1;
    transition: all 1s;
    .circle {
      margin-right: 0.7em;
      width: 1.1em;
      height: 1.1em;
      background-color: #fff;
      border-radius: 50%;
      animation: fade-pulse 1.4s cubic-bezier(0.075, 0.82, 0.165, 1) infinite;
    }
  }
  .video-player {
    position: relative;
    display: block;
    z-index: 100;
    width: 100%;
    height: 100%;
  }
  .video-navbar {
    position: absolute;
    height: 4em;
    display: flex;
    align-items: center;
    justify-content: space-between;
    bottom: 2em;
    opacity: 0;
    transition: all 1s;
    z-index: 120;
    .navbar-left {
      .volume-span {
        &:hover {
          button {
            i {
              color: rgb(255, 255, 255);
            }
          }
          input {
            opacity: 0.9;
          }
        }
      }
      span {
        &:first-child {
          display: flex;
          align-items: center;
          input {
            width: 6em;
            height: 3px;
            opacity: 0.9;
          }
        }
      }
      button:hover {
        i {
          color: rgb(255, 255, 255);
        }
      }
    }
    .navbar-right {
      button:hover {
        i {
          color: rgb(255, 255, 255);
        }
      }
    }
    .navbar-left,
    .navbar-right {
      height: 4em;
      display: flex;
      align-items: center;
      background: rgba(18, 18, 18, 0.85);
      transition: all 0.2s;
      button {
        cursor: pointer;
        background-color: inherit;
        position: relative;
        height: 68px;
        width: 60px;
        i {
          font-size: 1.65em;
          color: rgba(255, 255, 255, 0.5);
          transition: all 0.6s;
        }

        .hover-btn {
          position: absolute;
          top: -35px;
          padding: 5px 10px;
          background: rgba(18, 18, 18, 0.8);
          color: #ffffff;
          border-radius: 5px;
          opacity: 0;
          transition: all 0.4s;
          white-space: nowrap;
          transform: translateX(-50%);
          left: 50%;
        }
        &:hover .hover-btn {
          opacity: 1;
        }
      }
    }

    .navbar-left {
      width: 31.25em;
      justify-content: space-between;
      border-radius: 0.625em 0px 0px 0.625em;
      span {
        &:first-child {
          display: flex;
          align-items: center;
          input {
            width: 5em !important;
            height: 0.15em !important;
            -webkit-appearance: none;
            outline: none;
            border-radius: 13px;
            cursor: pointer;
            opacity: 0.5;
            transition: all 0.6s;
            &[type="range"]::-webkit-slider-thumb {
              -webkit-appearance: none;
              width: 13px;
              height: 13px;
              border-radius: 10px;
              box-sizing: content-box;
              background: white;
            }
          }
        }
        &:nth-child(2) {
          button {
            i {
              font-size: 2.5em;
            }
          }
        }
      }
    }
    .navbar-right {
      width: 4.33em;
      justify-content: center;
      margin-left: 5px;
      align-items: center;
      border-radius: 0px 10px 10px 0px;
      span {
        button {
          i {
            font-size: 2.2em;
          }
        }
      }
    }
  }
}
@media screen and (max-width: 992px) {
  .video-navbar {
    width: 100% !important;
    height: 100% !important;
    right: 0% !important;
    bottom: 0em !important;
  }
  .navbar-right {
    display: none !important;
  }
  .navbar-left {
    width: 100vw !important;
    height: 100% !important;
    justify-content: center !important;
    position: relative !important;
    align-items: center !important;
    background: rgba(18, 18, 18, 0) !important;
    .volume-span {
      display: none !important;
    }
    span {
      &:last-child {
        button {
          &:first-child {
            display: none !important;
          }
        }
      }
      &:last-child {
        button {
          width: auto !important;
          height: auto !important;
          position: absolute !important;
          bottom: 0em !important;
          right: 0em !important;
          transition: all 1s;
          width: 8em !important;
          height: 8em !important;
          i {
            font-size: 3em !important;
            color: rgba(255, 255, 255, 1) !important;
          }
        }
      }
      .hover-btn {
        display: none;
      }
    }
    .stop-pause {
      button {
        width: 10em !important;
        height: 10em !important;
        transition: all 1s;
        i {
          color: rgba(255, 255, 255, 1) !important;
          font-size: 6em !important;
        }
      }
    }
  }
}
</style>
